import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./NavBar.css";
import { useAuth } from "../../contexts/AuthProvider"; // Import the auth context
import Notification from "./Notifications"; // Ensure this component exists
import { useTranslation } from "react-i18next";
import { API_URL } from "../../utils/config"; // Import the API URL
const baseUrl = API_URL;

const Navbar = ({ setMenuToggle }) => {
  const { t } = useTranslation("navbar");
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();

  // Get user info from localStorage
  const userData = localStorage.getItem("user");
  const parsedUser = userData ? JSON.parse(userData) : null;

  const [activeDropdown, setActiveDropdown] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  // New states for notification functionality
  const [showNotification, setShowNotification] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const actionsDropdownRef = useRef(null);
  const notificationDropdownRef = useRef(null); // Ref for notification dropdown
  const prevNotificationCountRef = useRef(0); // Add ref to keep track of previous notification count

  // Calculate unread notifications count
  const unreadCount = notifications ? notifications.filter(notif => notif.is_read === 0).length : 0;

  const handleLogout = () => {
    setActiveDropdown(false);
    logout(); // use the context logout method
    navigate("/login");
  };

  const handleProfile = () => {
    navigate("/profile");
    setActiveDropdown(false);
  };

  const handleDropdown = () => {
    setActiveDropdown((prev) => !prev);
  };

  // Toggle notification dropdown
  const handleToggleNotifications = () => {
    setShowNotification((prev) => !prev);
  };
  
  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };


  // Fetch user profile on mount
  useEffect(() => {
    if (isAuthenticated) {
      fetch(`${baseUrl}/api/v1/attnd-supplier/auth/view-profile`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
      })
        .then(async (response) => {
          if (!response.ok) {
            if (response.status === 401 || response.status === 403) {
              await refreshToken();
            }
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          if (data && data.data) {
            setUserProfile(data.data);
          }
        })
        .catch((err) => console.error("Error fetching profile:", err));
    }
  }, [baseUrl]);

  // Fetch notifications every 30 seconds
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetch(`${baseUrl}/api/v1/notifications/supplier`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),
          },
        });

        if (!response.ok) {
          console.error("Failed to fetch notifications");
          if (response.status === 401 || response.status === 403) {
            await refreshToken();
          }

          return;
        }

        const data = await response.json();
        const notifications = Array.isArray(data)
          ? data
          : data.notifications || data.data || [];
        setNotifications(notifications);
       
      } catch (error) {
        console.error("Error fetching notifications count:", error);
      }
    };

    // Fetch notifications initially
    fetchNotifications();

    // Set interval to fetch notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [baseUrl, showNotification]);
// refresh token POST /api/v1/attnd-supplier/auth/refresh
const refreshToken = async () => {
  try {
    const response = await fetch(`${baseUrl}/api/v1/attnd-supplier/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        lang: getLangFromLocalStorage(),
      },
    });

    if (!response.ok) {
      console.error("Failed to refresh token");
      logout(); // Log out the user if token refresh fails
      return;
    }
  } catch (error) {
    console.error("Error refreshing token:", error);
  }
};
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (activeDropdown) {
        if (
          actionsDropdownRef.current &&
          !actionsDropdownRef.current.contains(event.target) &&
          !event.target.closest(".user")
        ) {
          setActiveDropdown(false);
        }
      }

      // Close notifications if click is outside the notification dropdown
      if (showNotification) {
        if (
          notificationDropdownRef.current &&
          !notificationDropdownRef.current.contains(event.target) &&
          !event.target.closest(".notification")
        ) {
          setShowNotification(false);
        }
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [activeDropdown, showNotification]);

  // Use userProfile if available
  const finalDisplayedName = parsedUser
    ? parsedUser.firstName
    : userProfile
    ? userProfile.firstName
    : t("guest");
  const finalDisplayedImage =
    (parsedUser && parsedUser.image) ||
    (userProfile && userProfile.image) ||
    "/default-user.png";

  return (
    <nav className="navbar-navbar">
      <div className="flex gap-[12px]">
        <button
          type="button"
          onClick={() => setMenuToggle((prev) => !prev)}
          className="menu-toggle"
        >
          <img src="/menu.svg" alt={t("menuToggle")} />
        </button>
        <div className="logo">
          <img src="/logo2.svg" alt={t("logoAlt")} />
        </div>
      </div>
      <div className="Search-container">
        <input
          type="text"
          className="search-input"
          placeholder={t("searchPlaceholder")}
        />
      </div>
      <div className="user-notification">
        <div className="notification" onClick={handleToggleNotifications}>
          <span className="notification-span">
           <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 20C0 8.95431 8.95431 0 20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20Z" fill="#F4F1FF"/>
              <path d="M20 18.5204C19.59 18.5204 19.25 18.1804 19.25 17.7704V14.4404C19.25 14.0304 19.59 13.6904 20 13.6904C20.41 13.6904 20.75 14.0304 20.75 14.4404V17.7704C20.75 18.1904 20.41 18.5204 20 18.5204Z" fill="#5007C5"/>
              <path d="M20.0208 28.3502C17.4408 28.3502 14.8708 27.9402 12.4208 27.1202C11.5108 26.8202 10.8208 26.1702 10.5208 25.3502C10.2208 24.5302 10.3208 23.5902 10.8108 22.7702L12.0808 20.6502C12.3608 20.1802 12.6108 19.3002 12.6108 18.7502V16.6502C12.6108 12.5602 15.9308 9.24023 20.0208 9.24023C24.1108 9.24023 27.4308 12.5602 27.4308 16.6502V18.7502C27.4308 19.2902 27.6808 20.1802 27.9608 20.6502L29.2308 22.7702C29.7008 23.5502 29.7808 24.4802 29.4708 25.3302C29.1608 26.1802 28.4808 26.8302 27.6208 27.1202C25.1708 27.9502 22.6008 28.3502 20.0208 28.3502ZM20.0208 10.7502C16.7608 10.7502 14.1108 13.4002 14.1108 16.6602V18.7602C14.1108 19.5702 13.7908 20.7402 13.3708 21.4302L12.1008 23.5602C11.8408 23.9902 11.7808 24.4502 11.9308 24.8502C12.0808 25.2502 12.4208 25.5502 12.9008 25.7102C17.5008 27.2402 22.5608 27.2402 27.1608 25.7102C27.5908 25.5702 27.9208 25.2502 28.0708 24.8302C28.2308 24.4102 28.1808 23.9502 27.9508 23.5602L26.6808 21.4402C26.2608 20.7502 25.9408 19.5802 25.9408 18.7702V16.6702C25.9308 13.4002 23.2808 10.7502 20.0208 10.7502Z" fill="#5007C5"/>
              <path d="M19.9999 30.9003C18.9299 30.9003 17.8799 30.4603 17.1199 29.7003C16.3599 28.9403 15.9199 27.8903 15.9199 26.8203H17.4199C17.4199 27.5003 17.6999 28.1603 18.1799 28.6403C18.6599 29.1203 19.3199 29.4003 19.9999 29.4003C21.4199 29.4003 22.5799 28.2403 22.5799 26.8203H24.0799C24.0799 29.0703 22.2499 30.9003 19.9999 30.9003Z" fill="#5007C5"/>
              {unreadCount > 0 && (
                <>
                  <circle cx="30" cy="16" r="7" fill="#5007C5"/>
                  <text x="30" y="20" textAnchor="middle" fill="#FCFCFC" fontSize="10" fontFamily="Arial, sans-serif" fontWeight="600">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </text>
                </>
              )}
            </svg>
          </span>
          <span>
            <img
              className="notification-span2"
              src={finalDisplayedImage}
              alt={t("notificationAlt")}
            />
          </span>
        </div>
        {showNotification && (
          <div className="notifications-dropdown" ref={notificationDropdownRef}>
            <Notification notifications={notifications} />
          </div>
        )}
        <button className="user" onClick={handleDropdown}>
          <h1>{finalDisplayedName}</h1>
          <img className="logout" src="/arrow-down.svg" alt="Dropdown" />
        </button>
        {activeDropdown && (
          <ul className="navbar-dropdown" ref={actionsDropdownRef}>
            <li onClick={handleProfile}>
              <button type="button">{t("profile")}</button>
            </li>
            <li onClick={handleLogout}>
              <button type="button">{t("logout")}</button>
            </li>
          </ul>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
